import { Combobox } from '@base-ui/react/combobox'
import { Select } from '@base-ui/react/select'
import { CaretDownIcon } from '@phosphor-icons/react'
import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { RefObject, useMemo, useState } from 'react'
import useSWR from 'swr'
import useSWRImmutable from 'swr/immutable'
import { useDebounceValue } from 'usehooks-ts'
import { flchUrl, get } from '@/src/BlinkAdminApiClient'

const TYPESENSE_HOST = 'https://search.blnk.ch'

const CATEGORIES = [
  { value: 'fahrschule', label: 'Fahrlehrer:innen' },
  { value: 'nothelferkurs', label: 'Nothelferkurs' },
  { value: 'vku', label: 'Verkehrskundeunterricht' },
  { value: 'motorradgrundkurs', label: 'Motorradgrundkurs' },
  { value: 'wab', label: 'WAB Kurs' },
] as const

type SearchConfig = {
  key: string
  collection: string
  urls: Record<string, string>
}

type SearchHit = {
  category: string
  name: string
  slug: string
  handle?: string
}

type LocationOption = {
  value: string
  label: string
  highlight: string
}

type Hit = {
  document: SearchHit
  highlight?: { name?: { value?: string; snippet?: string } }
}

async function searchLocations(
  config: SearchConfig,
  category: string,
  query: string
): Promise<LocationOption[]> {
  const filterBy =
    category === 'fahrschule'
      ? 'type:=teacher'
      : `type:=location && category:=${category}`

  const params = new URLSearchParams({
    q: query,
    query_by: 'name',
    filter_by: filterBy,
    prefix: 'true',
    per_page: '50',
    highlight_full_fields: 'name',
    highlight_start_tag: '<mark>',
    highlight_end_tag: '</mark>',
  })

  const response = await fetch(
    `${TYPESENSE_HOST}/collections/${config.collection}/documents/search?${params}`,
    { headers: { 'X-TYPESENSE-API-KEY': config.key } }
  )
  const data = await response.json()

  const seen = new Set<string>()
  const locations: LocationOption[] = []

  for (const hit of (data.hits ?? []) as Hit[]) {
    const { document } = hit
    let baseSlug: string
    if (category === 'fahrschule') {
      baseSlug = document.handle ?? document.slug
    } else {
      const prefix = config.urls[document.category] ?? ''
      baseSlug = document.slug.startsWith(prefix)
        ? document.slug.slice(prefix.length)
        : document.slug
    }

    if (seen.has(baseSlug)) continue
    seen.add(baseSlug)
    locations.push({
      value: baseSlug,
      label: document.name,
      highlight: hit.highlight?.name?.value ?? document.name,
    })
  }

  return locations
}

const DEFAULT_CATEGORY = 'fahrschule'

const Search = ({
  portalContainer,
  onNavigate,
  inputRef,
  defaultCategory,
}: {
  portalContainer?: RefObject<HTMLElement | null>
  onNavigate?: () => void
  inputRef?: RefObject<HTMLInputElement | null>
  defaultCategory?: string
} = {}) => {
  const router = useRouter()

  const [category, setCategory] = useState<string>(
    defaultCategory ?? DEFAULT_CATEGORY
  )
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<LocationOption | null>(null)
  const [debouncedQuery] = useDebounceValue(query, 250)
  const [comboboxOpen, setComboboxOpen] = useState(false)
  const [navigating, setNavigating] = useState(false)

  const { data: config } = useSWRImmutable<SearchConfig>(
    flchUrl('search-key'),
    get
  )

  const { data: locations = [], isLoading } = useSWR(
    config && debouncedQuery
      ? ['flch-locations', category, debouncedQuery]
      : null,
    () => searchLocations(config!, category, debouncedQuery)
  )

  const items = useMemo(() => {
    if (!selected || locations.some((l) => l.value === selected.value)) {
      return locations
    }
    return [selected, ...locations]
  }, [locations, selected])

  const prefix =
    category === 'fahrschule'
      ? ''
      : (config?.urls[category] ?? (category ? `${category}-` : ''))
  const hrefFor = (location: LocationOption) => `/${prefix}${location.value}`

  const navigateTo = async (location: LocationOption) => {
    if (navigating) return
    setNavigating(true)
    const navigated = await router.push(hrefFor(location))
    setNavigating(false)
    if (navigated) onNavigate?.()
  }

  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-lg">
      <div className="flex flex-col lg:flex-row lg:items-stretch">
        <Select.Root
          items={CATEGORIES}
          value={category}
          onValueChange={(value) => setCategory(value ?? DEFAULT_CATEGORY)}
          highlightItemOnHover={false}
        >
          <Select.Trigger
            className={clsx(
              'group flex h-14 min-w-0 items-center gap-2 rounded-t-xl px-4 lg:flex-1 lg:rounded-l-xl lg:rounded-tr-none',
              'ring-blue-600 ring-inset focus-visible:ring-2 focus-visible:outline-none'
            )}
          >
            <Select.Value className="truncate text-sm" />
            <Select.Icon className="ml-auto shrink-0">
              <CaretDownIcon
                size={20}
                className="text-gray-800 transition-transform group-data-popup-open:rotate-180"
              />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal container={portalContainer}>
            <Select.Positioner
              sideOffset={8}
              align="start"
              alignItemWithTrigger={false}
              className="z-50"
            >
              <Select.Popup
                className={clsx(
                  'divide-y divide-gray-150 overflow-hidden',
                  'rounded-lg border border-gray-300 bg-white shadow-xl',
                  'data-open:animate-in data-open:fade-in-0% data-open:zoom-in-95%',
                  'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                  'data-closed:animate-out data-closed:fade-out-0% data-closed:zoom-out-95%'
                )}
              >
                {CATEGORIES.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    className="flex h-10 cursor-pointer items-center px-4 whitespace-nowrap outline-none hover:bg-gray-200 data-highlighted:bg-gray-200 data-selected:bg-gray-200"
                  >
                    <Select.ItemText>{option.label}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>
        <div className="h-px w-full bg-gray-300 lg:h-auto lg:w-px" />
        <Combobox.Root
          items={items}
          value={selected}
          onValueChange={setSelected}
          inputValue={query}
          onInputValueChange={(value, details) => {
            setQuery(value)
            // Drop the selection once the user edits the query so a stale pick
            // isn't pinned in the dropdown.
            if (details.reason === 'input-change') setSelected(null)
          }}
          filter={null}
          autoHighlight={'always' as unknown as boolean}
          highlightItemOnHover={false}
          isItemEqualToValue={(a, b) => a?.value === b?.value}
          open={comboboxOpen}
          onOpenChange={(open) => {
            setComboboxOpen(open)
          }}
        >
          <div
            className={clsx(
              'relative flex h-14 min-w-0 lg:flex-1',
              'ring-blue-600 ring-inset focus-within:ring-2'
            )}
          >
            <Combobox.Input
              ref={inputRef}
              placeholder="Ort"
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || comboboxOpen) return
                if (selected) navigateTo(selected)
              }}
              className="h-full w-full border-0 bg-transparent pr-12 pl-4 text-sm placeholder:text-gray-600 focus:ring-0 focus:outline-none"
            />
          </div>
          <Combobox.Portal container={portalContainer}>
            <Combobox.Positioner sideOffset={8} align="start" className="z-50">
              <Combobox.Popup
                className={clsx(
                  'overflow-hidden',
                  'rounded-lg border border-gray-300 bg-white shadow-xl',
                  'data-open:animate-in data-open:fade-in-0% data-open:zoom-in-95%',
                  'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                  'data-closed:animate-out data-closed:fade-out-0% data-closed:zoom-out-95%',
                  'min-w-(--anchor-width) lg:min-w-[calc(var(--anchor-width)+80px)]'
                )}
              >
                <Combobox.Empty>
                  <p className="px-4 py-3 text-sm text-gray-600">
                    {!query.trim()
                      ? 'Bitte gib deinen Standort ein'
                      : isLoading || query !== debouncedQuery
                        ? 'Suche...'
                        : 'Keine Orte gefunden'}
                  </p>
                </Combobox.Empty>
                <Combobox.List className="max-h-72 divide-y divide-gray-150 overflow-y-auto">
                  {(location: LocationOption) => (
                    <Combobox.Item
                      key={location.value}
                      value={location}
                      className="flex h-10 cursor-pointer items-center px-4 whitespace-nowrap outline-none hover:bg-gray-200 data-highlighted:bg-gray-200 data-selected:bg-gray-200"
                    >
                      <p
                        className="[&_mark]:bg-transparent [&_mark]:font-bold [&_mark]:text-inherit"
                        dangerouslySetInnerHTML={{ __html: location.highlight }}
                      />
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Root>
        {selected ? (
          <Link
            href={hrefFor(selected)}
            onClick={(event) => {
              if (
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              ) {
                return
              }
              event.preventDefault()
              navigateTo(selected)
            }}
            aria-disabled={navigating || undefined}
            className={clsx(
              'flex h-14 shrink-0 items-center justify-center bg-blue-700 px-10 text-sm font-bold text-white transition-colors',
              navigating && 'cursor-wait'
            )}
          >
            {navigating && (
              <div className="animate-icon-slide-in overflow-hidden pr-2">
                <svg
                  className="size-4 shrink-0 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            )}
            Suchen
          </Link>
        ) : (
          <button
            type="button"
            disabled
            className="flex h-14 shrink-0 items-center justify-center bg-blue-700 px-10 text-sm font-bold text-white transition-colors"
          >
            Suchen
          </button>
        )}
      </div>
    </div>
  )
}

export default Search
